import { useState } from 'react';
import { Link } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { Avatar } from '@/components/brand/Brand';
import { useToast } from '@/components/ui/Toast';
import { socialApi } from '@/lib/api/endpoints';
import { qk, useRoundComments } from '@/lib/api/queries';
import type { FeedItem } from '@/lib/api/types';
import { cx } from '@/lib/cx';
import { fmtStartedAt } from '@/lib/format/date';
import { fmtDecimal, fmtInt, fmtPct, fmtToPar } from '@/lib/format/number';
import { routes } from '@/routes/paths';
import { t } from '@/lib/i18n';
import styles from './FeedCard.module.css';
import { ClapIcon, CloseIcon, MessageCircleIcon } from '@/components/icons';

interface FeedCardProps {
  item: FeedItem;
  courseName: string;
  /** Id do usuário logado — decide quem pode apagar cada comentário. */
  viewerId: string;
}

/**
 * Um cartão da timeline (canvas 1p). O `switch` por `type` é exaustivo de propósito: quando a
 * Fase B acrescentar `group_round` à união, o compilador aponta exatamente este ponto.
 */
export function FeedCard({ item, courseName, viewerId }: FeedCardProps) {
  const isYou = item.actor.id === viewerId;

  return (
    <article className={cx(styles.card, isYou && styles.cardYou)}>
      <div className={styles.head}>
        {/*
          O marcador de "você" do canvas: faixa dourada à esquerda + avatar preenchido, sempre
          essa dupla. Nos outros o avatar é verde — o `outline` padrão é desenhado para fundo
          escuro (borda clara translúcida sobre `on-ink`) e some num cartão branco.
        */}
        <Avatar name={item.actor.displayName} size={38} variant={isYou ? 'gold' : 'green'} />
        <div className={styles.headBody}>
          {/*
            O nome leva ao perfil daquele jogador (canvas 3a) — menos o seu, que levaria de volta
            para uma tela sobre você mesmo, com o seu handicap comparado ao seu handicap.
          */}
          <div className={styles.author}>
            {isYou ? (
              item.actor.displayName
            ) : (
              <Link to={routes.player(item.actor.id)} className={styles.authorLink}>
                {item.actor.displayName}
              </Link>
            )}
          </div>
          <div className={styles.meta}>{describeMeta(item, courseName)}</div>
        </div>
      </div>

      <div className={styles.body}>
        <CardBody item={item} courseName={courseName} />
      </div>

      <SocialFooter item={item} viewerId={viewerId} />
    </article>
  );
}

function CardBody({ item, courseName }: { item: FeedItem; courseName: string }) {
  switch (item.type) {
    case 'live_round':
      return (
        <>
          <span className={styles.liveTag}>
            <span className={styles.liveDot} aria-hidden="true" />
            {t('feed.playingNow')}
          </span>
          <div className={styles.liveRow}>
            <span className={styles.liveWhere}>
              {t('feed.liveWhere', {
                course: courseName,
                done: item.holesCompleted,
                total: item.holesPlayed,
              })}
            </span>
            <span className={styles.liveNumbers}>
              {/*
                No desktop o progresso sobe para esta linha (canvas 1u) e o rótulo sob a barra
                some: com largura, o número cabe ao lado do que ele mede, e a barra deixa de
                precisar de legenda própria.
              */}
              <span className={styles.livePct}>{item.progressPct}%</span>
              <span>
                <span className={styles.liveToPar}>{fmtToPar(item.toPar)}</span>{' '}
                <span className={styles.liveToParLabel}>{t('feed.toPar')}</span>
              </span>
            </span>
          </div>
          <div
            className={styles.progressTrack}
            role="progressbar"
            aria-valuenow={item.progressPct}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={t('feed.progressAria')}
          >
            <div className={styles.progressFill} style={{ width: `${item.progressPct}%` }} />
          </div>
          <div className={styles.progressLabel}>{item.progressPct}%</div>

          {/*
            O convite (canvas 2a). Ele não é um cartão à parte nem uma notificação: é este mesmo
            cartão ao vivo ganhando um botão, e só para quem foi incluído no flight — quem não
            foi recebe `joinableGroupRoundId` nulo e vê o cartão de sempre.
          */}
          {item.joinableGroupRoundId && (
            <Link
              to={routes.joinFlight(item.joinableGroupRoundId)}
              className={styles.joinCta}
            >
              <span className={styles.joinCtaHint}>{t('feed.joinFlightHint')}</span>
              <span className={styles.joinCtaAction}>{t('feed.joinFlight')}</span>
            </Link>
          )}
        </>
      );

    case 'handicap_milestone':
      return (
        <Link to={routes.round(item.roundId)} className={styles.milestone}>
          {t('feed.loweredHandicap')}
          <strong className={styles.milestoneValue}>{fmtDecimal(item.handicapIndex)}</strong>
          <span className={styles.milestoneNote}>
            {item.isPersonalBest
              ? t('feed.personalBest')
              : t('feed.wasBefore', { value: fmtDecimal(item.previousHandicapIndex) })}
          </span>
        </Link>
      );

    case 'completed_round':
      return (
        <Link to={routes.round(item.roundId)}>
          <div className={styles.course}>{courseName}</div>
          <div className={styles.scoreRow}>
            <div className={styles.stats}>
              <div>
                <div className={styles.statLabel}>{t('round.putts')}</div>
                <div className={styles.statValue}>{fmtInt(item.totalPutts)}</div>
              </div>
              <div>
                <div className={styles.statLabel}>{t('round.gir')}</div>
                <div className={styles.statValue}>{fmtPct(item.greensInRegulationPct)}</div>
              </div>
              {/* A quarta estatística só existe no desktop — é o "mais dado por cartão" de 1u. */}
              <div className={styles.statWide}>
                <div className={styles.statLabel}>{t('scorecard.fw')}</div>
                <div className={styles.statValue}>{fmtPct(item.fairwayHitPct)}</div>
              </div>
              <div>
                <div className={styles.statLabel}>{t('rounds.table.sd')}</div>
                <div className={styles.statValue}>{fmtDecimal(item.scoreDifferential)}</div>
              </div>
            </div>
            <div className={styles.score}>
              <div className={styles.scoreValue}>{fmtInt(item.totalStrokes)}</div>
              <div className={styles.scoreToPar}>{fmtToPar(item.totalToPar)}</div>
            </div>
          </div>
        </Link>
      );
  }
}

/**
 * "Bom jogo" e "Comentar". A reação é **nomeada e única** — o canvas descarta a paleta de emoji,
 * e uma reação por pessoa por volta é o que faz tocar de novo significar desfazer.
 */
function SocialFooter({ item, viewerId }: { item: FeedItem; viewerId: string }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState('');
  const [working, setWorking] = useState(false);
  const comments = useRoundComments(item.roundId, open);
  const toast = useToast();
  const queryClient = useQueryClient();

  const refreshFeed = () => queryClient.invalidateQueries({ queryKey: ['feed'] });

  const react = async () => {
    setWorking(true);
    try {
      await socialApi.toggleReaction(item.roundId);
      await refreshFeed();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t('feed.reactFailed'));
    } finally {
      setWorking(false);
    }
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const body = draft.trim();
    if (!body) return;
    setWorking(true);
    try {
      await socialApi.addComment(item.roundId, body);
      setDraft('');
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: qk.comments(item.roundId) }),
        refreshFeed(),
      ]);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('feed.commentFailed'));
    } finally {
      setWorking(false);
    }
  };

  const remove = async (commentId: string) => {
    try {
      await socialApi.deleteComment(item.roundId, commentId);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: qk.comments(item.roundId) }),
        refreshFeed(),
      ]);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t('feed.deleteFailed'));
    }
  };

  return (
    <>
      {/*
        Ícone no lugar da palavra, e o número fica: é ele que mostra de relance em quais voltas há
        conversa. O `aria-label` carrega a frase inteira — "Bom jogo · 3" —, porque o nome acessível
        vence o conteúdo do botão e um "3" solto não diria nada a quem ouve a tela. É também o que
        mantém `feed.niceRound`, `feed.comment` e `feed.withCount` vivos no catálogo.
      */}
      <div className={styles.actions}>
        <button
          type="button"
          className={cx(styles.action, item.viewerReacted && styles.actionOn)}
          onClick={() => void react()}
          disabled={working}
          aria-pressed={item.viewerReacted}
          aria-label={
            item.reactionCount > 0
              ? t('feed.withCount', { label: t('feed.niceRound'), count: item.reactionCount })
              : t('feed.niceRound')
          }
        >
          <ClapIcon size={16} />
          {item.reactionCount > 0 && item.reactionCount}
        </button>
        <button
          type="button"
          className={styles.action}
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-label={
            item.commentCount > 0
              ? t('feed.withCount', { label: t('feed.comment'), count: item.commentCount })
              : t('feed.comment')
          }
        >
          <MessageCircleIcon size={16} />
          {item.commentCount > 0 && item.commentCount}
        </button>
      </div>

      {open && (
        <div className={styles.comments}>
          {comments.data?.map((c) => (
            <div key={c.id} className={styles.comment}>
              {c.author.id === viewerId ? (
                <span className={styles.commentAuthor}>{c.author.displayName}</span>
              ) : (
                <Link to={routes.player(c.author.id)} className={styles.commentAuthor}>
                  {c.author.displayName}
                </Link>
              )}
              <span>{c.body}</span>
              {(c.author.id === viewerId || item.actor.id === viewerId) && (
                <button
                  type="button"
                  className={styles.commentDelete}
                  onClick={() => void remove(c.id)}
                  aria-label={t('feed.deleteComment', { name: c.author.displayName })}
                >
                  <CloseIcon size={18} />
                </button>
              )}
            </div>
          ))}
          <form className={styles.commentForm} onSubmit={(e) => void submit(e)}>
            <input
              className={styles.commentInput}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder={t('feed.writeComment')}
              aria-label={t('feed.writeComment')}
              maxLength={500}
            />
            <button type="submit" className={styles.action} disabled={working || !draft.trim()}>
              {t('feed.send')}
            </button>
          </form>
        </div>
      )}
    </>
  );
}

/** "ontem · Serra Azul · Tee Vermelho" — a linha de contexto sob o nome. */
function describeMeta(item: FeedItem, courseName: string): string {
  const when = fmtStartedAt(item.occurredAt);
  if (item.type === 'handicap_milestone') return when;
  return t('feed.meta', { when, course: courseName, tee: item.teeBoxName });
}
